import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo, useCallback, useImperativeHandle } from 'react';
import { EditorSelector } from '../../../Components/EditorSelector.js';
import { modifyNodeItem } from '../../../ui/Toolbar/toolbarConfig.js';
import { ContextualMenuMeta } from '../ContextualMenuMeta.js';
import { TextSelection } from '@tiptap/pm/state';
import { IconMobileSheet24TextEditorAddRowBefore, IconTextEditor16AddRowBefore, IconMobileSheet24TextEditorAddRowAfter, IconTextEditor16AddRowAfter, IconMobileSheet24TextEditorAddColumnBefore, IconTextEditor16AddColumnBefore, IconMobileSheet24TextEditorAddColumnAfter, IconTextEditor16AddColumnAfter, IconMobileSheet24TextEditorMergeSplitCells, IconTextEditor16MergeSplitCells, IconMobileSheet24TextEditorToggleHeaderColumn, IconTextEditor16ToggleHeaderColumn, IconMobileSheet24TextEditorToggleHeaderRow, IconTextEditor16ToggleHeaderRow, IconMobileSheet24TextEditorToggleHeaderCell, IconTextEditor16ToggleHeaderCell, IconMobileSheet24TextEditorDeleteRow, IconTextEditor16DeleteRow, IconMobileSheet24TextEditorDeleteColumn, IconTextEditor16DeleteColumn, IconMobileSheet24TextEditorUndo, IconTextEditor16Undo, IconMobileSheet24TextEditorRedo, IconTextEditor16Redo, IconTextEditor16Delete } from '@fabric-app/icons/svg';
import { undo, redo } from 'y-prosemirror';
import { ContextualMenuPluginKey } from '../ContextualMenuPlugin.js';
import { getNodeNameAction } from '../../utils.js';
import { isDefined } from '@fabric-app/utils/guards';

const tableActionGroup = {
  items: [
    {
      value: "addRowBefore",
      label: "Add row before",
      icon: /* @__PURE__ */ jsx(IconTextEditor16AddRowBefore, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorAddRowBefore, {})
    },
    {
      value: "addRowAfter",
      label: "Add row after",
      icon: /* @__PURE__ */ jsx(IconTextEditor16AddRowAfter, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorAddRowAfter, {})
    },
    {
      value: "addColumnBefore",
      label: "Add column before",
      icon: /* @__PURE__ */ jsx(IconTextEditor16AddColumnBefore, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorAddColumnBefore, {})
    },
    {
      value: "addColumnAfter",
      label: "Add column after",
      icon: /* @__PURE__ */ jsx(IconTextEditor16AddColumnAfter, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorAddColumnAfter, {})
    },
    {
      value: "mergeSplitCells",
      label: "Merge/Split cells",
      icon: /* @__PURE__ */ jsx(IconTextEditor16MergeSplitCells, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorMergeSplitCells, {})
    },
    {
      value: "toggleHeaderColumn",
      label: "Toggle header column",
      icon: /* @__PURE__ */ jsx(IconTextEditor16ToggleHeaderColumn, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorToggleHeaderColumn, {})
    },
    {
      value: "toggleHeaderRow",
      label: "Toggle header row",
      icon: /* @__PURE__ */ jsx(IconTextEditor16ToggleHeaderRow, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorToggleHeaderRow, {})
    },
    {
      value: "toggleHeaderCell",
      label: "Toggle header cell",
      icon: /* @__PURE__ */ jsx(IconTextEditor16ToggleHeaderCell, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorToggleHeaderCell, {})
    },
    {
      value: "deleteRow",
      label: "Delete row",
      icon: /* @__PURE__ */ jsx(IconTextEditor16DeleteRow, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorDeleteRow, {})
    },
    {
      value: "deleteColumn",
      label: "Delete column",
      icon: /* @__PURE__ */ jsx(IconTextEditor16DeleteColumn, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorDeleteColumn, {})
    }
  ]
};
const modifyNodeActionGroup = {
  key: "action",
  label: "Actions",
  items: [
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Undo, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorUndo, {}),
      label: "Undo",
      value: "undo"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Redo, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorRedo, {}),
      label: "Redo",
      value: "redo"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Delete, {}),
      label: "Delete",
      value: "delete"
    }
  ]
};
const doTableAction = (editor, item) => {
  switch (item) {
    case "addRowBefore":
      editor.chain().focus().addRowBefore().run();
      return true;
    case "addRowAfter":
      editor.chain().focus().addRowAfter().run();
      return true;
    case "addColumnBefore":
      editor.chain().focus().addColumnBefore().run();
      return true;
    case "addColumnAfter":
      editor.chain().focus().addColumnAfter().run();
      return true;
    case "mergeSplitCells":
      editor.chain().focus().mergeOrSplit().run();
      return true;
    case "toggleHeaderColumn":
      editor.chain().focus().toggleHeaderColumn().run();
      return true;
    case "toggleHeaderRow":
      editor.chain().focus().toggleHeaderRow().run();
      return true;
    case "toggleHeaderCell":
      editor.chain().focus().toggleHeaderCell().run();
      return true;
    case "deleteRow":
      editor.chain().focus().deleteRow().run();
      return true;
    case "deleteColumn":
      editor.chain().focus().deleteColumn().run();
      return true;
  }
  return false;
};
const ContextualModifyItemMenu = /* @__PURE__ */ forwardRef(({ state: { view, selection }, editor }, ref) => {
  const node = useMemo(() => {
    const contextualState = ContextualMenuPluginKey.getState(view.state);
    if (!contextualState || !isDefined(contextualState.range.from)) return null;
    return view.state.doc.nodeAt(contextualState.range.from);
  }, [
    view.state
  ]);
  const showTransformOptions = useMemo(() => {
    if (!node) return false;
    const nodeName = getNodeNameAction(node);
    return modifyNodeItem.options.some((option) => option.value === nodeName);
  }, [
    node
  ]);
  const onSelect = useCallback((item) => {
    const contextualState = ContextualMenuPluginKey.getState(view.state);
    if (!contextualState || !isDefined(contextualState.range.from)) return;
    ContextualMenuMeta.reset(view);
    view.focus();
    if (doTableAction(editor, item)) {
      return;
    }
    if (item === "delete") {
      const tr2 = view.state.tr;
      tr2.deleteRange(contextualState.range.from, contextualState.range.to);
      view.dispatch(tr2);
      return;
    }
    if (item === "undo") {
      undo(view.state);
      return;
    }
    if (item === "redo") {
      redo(view.state);
      return;
    }
    const selection2 = view.state.selection;
    const from = selection2.from > contextualState.range.from && selection2.from < contextualState.range.to ? selection2.from : contextualState.range.from;
    const to = selection2.to < contextualState.range.to && selection2.to > contextualState.range.from ? selection2.to : contextualState.range.to;
    const tr = view.state.tr;
    tr.setSelection(TextSelection.create(view.state.doc, from, to));
    view.dispatch(tr);
    modifyNodeItem.onSelect?.(editor, item);
  }, [
    editor,
    view
  ]);
  useImperativeHandle(ref, () => ({
    onSelect
  }));
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      showTransformOptions && /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
            children: "Turn into:"
          }),
          modifyNodeItem.options.map((option) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              "data-keyboard-select": option.value === "selection",
              onClick: () => onSelect(option.value),
              value: option.value,
              label: option.label,
              children: option.icon
            }, option.value);
          })
        ]
      }),
      node?.type.name === "table" && /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
            children: "Table:"
          }),
          tableActionGroup.items.map((item) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              value: item.value,
              label: item.label,
              onClick: () => onSelect(item.value),
              children: item.icon
            }, item.value);
          })
        ]
      }),
      /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
            children: "Actions:"
          }),
          modifyNodeActionGroup.items.map((item) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              "data-keyboard-selected": item.value === selection,
              onClick: () => onSelect(item.value),
              value: item.value,
              label: item.label,
              "data-delete": item.value === "delete",
              children: item.icon
            }, item.value);
          })
        ]
      })
    ]
  });
});
ContextualModifyItemMenu.displayName = "ContextualModifyItemMenu";

export { ContextualModifyItemMenu as default, modifyNodeActionGroup };
//# sourceMappingURL=ContextualModifyItemMenu.js.map
